import { makeChoices } from "../../distractors";
import { text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

/** Numbers around a mean whose deviations sum to zero, so the mean is an integer. */
function numbersWithMean(rng: Rng, count: number, mean: number, spread: number): number[] {
  const deviations: number[] = [];
  for (let i = 0; i < count - 1; i++) deviations.push(rng.int(-spread, spread));
  deviations.push(-deviations.reduce((a, b) => a + b, 0));
  return rng.shuffle(deviations.map((d) => mean + d));
}

const list = (nums: number[]) => nums.join(", ");

function tier1(rng: Rng): QuestionDraft {
  const mean = rng.int(5, 30);
  const nums = numbersWithMean(rng, 3, mean, 4);
  const sum = nums.reduce((a, b) => a + b, 0);
  const choice4 = makeChoices(rng, mean, [sum, sum / 2, mean + 1, mean - 1], (r) => r.int(2, 40));
  return q(
    both(`Oblicz średnią arytmetyczną liczb: ${list(nums)}.`, `Find the mean of the numbers: ${list(nums)}.`),
    numeric(mean),
    [`Dodajemy: ${nums.join(" + ")} = ${sum}.`, `Dzielimy przez ilość liczb: ${sum} : 3 = ${mean}.`],
    [`Add them: ${nums.join(" + ")} = ${sum}.`, `Divide by how many numbers: ${sum} ÷ 3 = ${mean}.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const count = rng.pick([4, 5]);
  const mean = rng.int(6, 50);
  const nums = numbersWithMean(rng, count, mean, 6);
  const sum = nums.reduce((a, b) => a + b, 0);
  const choice4 = makeChoices(rng, mean, [Math.round(sum / (count - 1)), sum, mean + 2, mean - 2], (r) =>
    r.int(2, 60),
  );
  const contextPl = rng.pick([
    `W ${count} walkach bohater zdobył kolejno ${list(nums)} punktów doświadczenia.`,
    `Milo sprzedał przez ${count} dni po ${list(nums)} mikstur.`,
  ]);
  const contextEn = contextPl.startsWith("W ")
    ? `In ${count} battles the hero earned ${list(nums)} experience points.`
    : `Over ${count} days Milo sold ${list(nums)} potions.`;
  return q(
    both(`${contextPl} Jaka jest średnia?`, `${contextEn} What is the average?`),
    numeric(mean),
    [`Suma: ${nums.join(" + ")} = ${sum}.`, `Średnia: ${sum} : ${count} = ${mean}.`],
    [`Sum: ${nums.join(" + ")} = ${sum}.`, `Mean: ${sum} ÷ ${count} = ${mean}.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const count = 4;
  const mean = rng.int(8, 40);
  const nums = numbersWithMean(rng, count, mean, 7);
  const known = nums.slice(0, 3);
  const missing = nums[3] as number;
  const knownSum = known.reduce((a, b) => a + b, 0);
  const choice4 = makeChoices(
    rng,
    missing,
    [mean, mean * count, mean * count - knownSum + mean, missing + 4],
    (r) => r.int(1, 60),
  );
  return q(
    both(
      `Średnia czterech liczb wynosi ${mean}. Trzy z nich to ${list(known)}. Jaka jest czwarta liczba?`,
      `The mean of four numbers is ${mean}. Three of them are ${list(known)}. What is the fourth number?`,
    ),
    numeric(missing),
    [
      `Suma wszystkich czterech: ${mean} · 4 = ${mean * count}.`,
      `Suma znanych: ${known.join(" + ")} = ${knownSum}.`,
      `${mean * count} − ${knownSum} = ${missing}.`,
    ],
    [
      `Sum of all four: ${mean} × 4 = ${mean * count}.`,
      `Sum of the known ones: ${known.join(" + ")} = ${knownSum}.`,
      `${mean * count} − ${knownSum} = ${missing}.`,
    ],
    { choice4 },
  );
}

export const average: TopicDef = {
  id: "sp6.average",
  level: "sp6",
  name: text("Średnia arytmetyczna", "Arithmetic mean"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
